import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    FiHome,
    FiFileText,
    FiCalendar,
    FiCheckSquare,
    FiBox,
    FiSettings,
    FiLogOut,
    FiChevronDown,
    FiMenu,
    FiUser,
    FiDatabase,
    FiShoppingCart,
    FiUsers,
    FiDollarSign,
    FiFolder,
    FiTool,
    FiPenTool
} from 'react-icons/fi';
import {
    isAdmin,
    isPlanner,
    isQCAdmin,
    isSuperAdmin,
    canAccessPlanningModule,
    canAccessQCModule,
    canAccessEngineeringModule,
    canAccessToolingModule,
    canAccessToolingManagement,
    canAccessAccounting,
    canAccessInventoryPortalItem,
    canAccessSalesModule,
    canAccessPurchaseModule,
    canAccessModuleAccessibility,
    isBusinessUnitUser,
    isShippingUser,
    isBusinessUnitManager,
    isPurchasingUser,
    isPurchasingManager,
} from '../utils/roleConstants';
import { canAccessUserSettings } from '../utils/userSettingsAccess';
import { canAccessApprovalRouting } from '../utils/approvalRoutingAccess';
import { isSidebarRouteActive } from '../utils/sidebarRouteMatch';
import logo from '../Assets/dataco_logo-NEW.png';
import logoText from '../Assets/Elexer-logo-text-only.png';
import '../Assets/Sidebar.css';

const Sidebar = ({ user, pendingApprovalCount, pendingCountsByType, pendingSalesCount, pendingPurchaseCount, pendingFinanceCount }) => {
    const location = useLocation();
    const [collapsed, setCollapsed] = useState(false);
    const [openMenus, setOpenMenus] = useState({});
    const [mobileOpen, setMobileOpen] = useState(false);

    const toggleMenu = (menu) => {
        if (collapsed) return;
        setOpenMenus(prev => ({
            ...prev,
            [menu]: !prev[menu]
        }));
    };

    const isActive = (path) => {
        return isSidebarRouteActive(location, path);
    };

    const isSubmenuActive = (links) => {
        return links.some(link => {
            if (link.to && isActive(link.to)) return true;
            if (link.subLinks && isSubmenuActive(link.subLinks)) return true;
            return false;
        });
    };

    const formatBadge = (badge) => {
        return badge > 99 ? '99+' : badge;
    };

    const getSubmenuKey = (parentId, link, index) => {
        const label = link.text ? link.text.toLowerCase().replace(/[^a-z0-9]+/g, '-') : 'submenu';
        return `${parentId}-${label}-${index}`;
    };

    const getSubmenuRailLeft = (level) => {
        return `${34 + ((level - 1) * 20)}px`;
    };

    const getSubmenuPadding = (level) => {
        return `${60 + ((level - 1) * 20)}px`;
    };

    const toggleSidebar = () => {
        const nextCollapsed = !collapsed;
        setCollapsed(nextCollapsed);
        if (nextCollapsed) {
            setOpenMenus({});
        }
    };

    const closeMobileSidebar = () => {
        setMobileOpen(false);
    };

    const renderNavLink = (to, icon, text, badge = null) => (
        <Link
            to={to}
            className={`nav-link-custom ${isActive(to) ? 'active' : ''}`}
            onClick={closeMobileSidebar}
        >
            <div className="nav-icon">{icon}</div>
            <span className="nav-text">{text}</span>
            <span className="tooltip-text">{text}</span>
            {badge > 0 && <span className="badge-custom">{formatBadge(badge)}</span>}
        </Link>
    );

    const renderExpandedSubmenuItems = (links, parentId, level = 1) => {
        return links.map((link, index) => {
            if (link.divider) {
                return <div key={index} className="submenu-divider" />;
            }

            const itemStyle = {
                '--submenu-link-padding': getSubmenuPadding(level)
            };

            if (link.subLinks) {
                const nestedId = getSubmenuKey(parentId, link, index);
                const isNestedOpen = !collapsed && openMenus[nestedId];
                const isNestedActive = isSubmenuActive(link.subLinks);

                return (
                    <div key={index} className="nested-submenu-container">
                        <div
                            className={`submenu-link nested-trigger ${isNestedActive ? 'active' : ''}`}
                            style={itemStyle}
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleMenu(nestedId);
                            }}
                        >
                            <span className="submenu-label">{link.text}</span>
                            {link.badge > 0 && <span className="badge-custom">{formatBadge(link.badge)}</span>}
                            <FiChevronDown className={`nested-arrow ${isNestedOpen ? 'open' : ''}`} />
                        </div>
                        <div
                            className={`nested-submenu ${isNestedOpen ? 'open' : ''}`}
                            style={{ '--submenu-rail-left': getSubmenuRailLeft(level + 1) }}
                        >
                            {renderExpandedSubmenuItems(link.subLinks, nestedId, level + 1)}
                        </div>
                    </div>
                );
            }

            const itemClassName = `submenu-link ${isActive(link.to) ? 'active' : ''} ${!link.to ? 'disabled' : ''}`;
            const itemContent = (
                <>
                    <span className="submenu-label">{link.text}</span>
                    {link.badge > 0 && <span className="badge-custom">{formatBadge(link.badge)}</span>}
                </>
            );

            return link.to ? (
                <Link
                    key={index}
                    to={link.to}
                    className={itemClassName}
                    style={itemStyle}
                    onClick={closeMobileSidebar}
                >
                    {itemContent}
                </Link>
            ) : (
                <div key={index} className={itemClassName} style={itemStyle}>
                    {itemContent}
                </div>
            );
        });
    };

    const renderFloatingSubmenuItems = (links, parentId, level = 1) => {
        return links.map((link, index) => {
            if (link.divider) {
                return <div key={index} className="submenu-divider" />;
            }

            if (link.subLinks) {
                const nestedId = getSubmenuKey(parentId, link, index);

                return (
                    <div key={index} className="floating-nested-section" style={{ '--floating-submenu-level': level }}>
                        <div className="floating-nested-title">
                            {link.text}
                            {link.badge > 0 && <span className="badge-custom">{formatBadge(link.badge)}</span>}
                        </div>
                        <div className="floating-nested-content">
                            {renderFloatingSubmenuItems(link.subLinks, nestedId, level + 1)}
                        </div>
                    </div>
                );
            }

            const itemClassName = `submenu-link ${isActive(link.to) ? 'active' : ''} ${!link.to ? 'disabled' : ''}`;
            const itemContent = (
                <>
                    <span className="submenu-label">{link.text}</span>
                    {link.badge > 0 && <span className="badge-custom">{formatBadge(link.badge)}</span>}
                </>
            );

            return link.to ? (
                <Link key={index} to={link.to} className={itemClassName} onClick={closeMobileSidebar}>
                    {itemContent}
                </Link>
            ) : (
                <div key={index} className={itemClassName}>
                    {itemContent}
                </div>
            );
        });
    };

    const renderSubmenu = (id, icon, text, links, badge = null) => {
        const isOpen = !collapsed && openMenus[id];
        const isAnyActive = isSubmenuActive(links);

        return (
            <div className="nav-item">
                <div
                    className={`nav-link-custom ${isAnyActive ? 'active' : ''}`}
                    onClick={() => toggleMenu(id)}
                >
                    <div className="nav-icon">{icon}</div>
                    <span className="nav-text">{text}</span>
                    <span className="tooltip-text">{text}</span>
                    {badge > 0 && <span className="badge-custom">{formatBadge(badge)}</span>}
                    <FiChevronDown className={`nav-arrow ${isOpen ? 'open' : ''}`} />
                </div>

                {/* Standard Submenu */}
                <div
                    className={`submenu ${isOpen ? 'open' : ''}`}
                    style={{ '--submenu-rail-left': getSubmenuRailLeft(1) }}
                >
                    {renderExpandedSubmenuItems(links, id)}
                </div>

                {/* Floating Submenu for Collapsed State */}
                {collapsed && (
                    <div className="floating-submenu">
                        <div className="floating-header">{text}</div>
                        <div className="floating-content">
                            {renderFloatingSubmenuItems(links, id)}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const toolingLinks = canAccessToolingManagement(user)
        ? [
            { to: "/tooling/master-data", text: "Master Data" },
            { to: "/tooling/maintenance-summary", text: "Maintenance Summary" },
            { to: "/tooling/tpm", text: "TPM" }
        ]
        : [];

    return (
        <>
            <button className="mobile-toggle d-lg-none" onClick={() => setMobileOpen(true)}>
                <FiMenu size={24} />
            </button>

            <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
                <div className="sidebar-header">
                    <Link to="/" className="sidebar-logo">
                        <img src={logo} alt="Logo" />
                        {!collapsed && <img className="sidebar-logo-text" src={logoText} alt="Elexer" />}
                    </Link>
                    <button className="sidebar-toggle d-none d-lg-flex" onClick={toggleSidebar}>
                        <FiMenu size={20} />
                    </button>
                </div>

                <div className="sidebar-content">
                    {renderNavLink("/", <FiHome />, "Dashboard")}

                    {/* Reports Section */}
                    {renderSubmenu("reports", <FiFileText />, "Reports", [
                        {
                            text: "Planning",
                            subLinks: [
                                { to: "/jobOrderSummaryReport", text: "Job Order Summary" }
                            ]
                        },
                        {
                            text: "Production",
                            subLinks: [
                                { to: "/hourlyreport", text: "Hourly Report" },
                                { to: "/dailyoeereport", text: "Daily OEE" },
                                { to: "/dailyproductivityreport", text: "Daily Productivity" },
                                { to: "/monthlyoeereport", text: "Monthly OEE" },
                                { to: "/monthlyproductivityreport", text: "Monthly Productivity" }
                            ]
                        },
                        ...(canAccessInventoryPortalItem(user, 'reports-all') ? [{
                            text: "Logistic",
                            subLinks: [
                                { to: "/rm-warehouse-occupancy-report", text: "RM Occupancy" },
                                { to: "/fg-warehouse-occupancy-report", text: "FG Occupancy" },
                                { to: "/inventory-status-overview", text: "Inventory Overview" },
                                { to: "/inventory-bom-usage-report", text: "Material Usage" },
                                { to: "/inventory-status-report", text: "Inventory Status" }
                            ]
                        }] : [])
                    ])}

                    {/* Planning Section */}
                    {(canAccessPlanningModule(user) || user.username === 'test') &&
                        renderSubmenu("planning", <FiCalendar />, "Planning", [
                            ...(user.allowPlanning === true && user.accountId === 2 ? [{ to: "/upload", text: "Upload" }] : []),
                            ...(isPlanner(user) ? [{ to: "/masterdata?mode=view", text: "View BOM" }] : []),
                            { to: "/planning", text: "Planning" },
                            { to: "/clear-to-build", text: "Clear to Build" },
                            { to: "/mrp", text: "MRP" }
                        ])
                    }

                    {/* QC Section (Q Value) */}
                    {(canAccessQCModule(user) || user.username === 'test') &&
                        renderSubmenu("qc", <FiCheckSquare />, "Q Value", [
                            {
                                text: "Master Data",
                                subLinks: [
                                    { to: "/incoming_master", text: "In-Coming Master" },
                                    { to: "/inprogress_master", text: "In-Progress Master" },
                                    { to: "/outgoing_master", text: "Outgoing Master" },
                                    { to: "/fai_master", text: "FAI Master" }
                                ]
                            },
                            {
                                text: "Summary",
                                subLinks: [
                                    { to: "/incoming_summary", text: "In-Coming Summary", badge: pendingCountsByType.incoming },
                                    { to: "/inprogress_summary", text: "In-Progress Summary", badge: pendingCountsByType.inprogress },
                                    { to: "/outgoing_summary", text: "Out-Going Summary", badge: pendingCountsByType.outgoing },
                                    { to: "/fpbo_summary", text: "FPBO Summary", badge: pendingCountsByType.fpbo },
                                    { to: "/lpbo_summary", text: "LPBO Summary", badge: pendingCountsByType.lpbo },
                                    { to: "/fai_summary", text: "FAI Summary", badge: pendingCountsByType.fai },
                                    { to: "/waiting-for-inspection", text: "Waiting for Inspection" }
                                ]
                            },
                        ], pendingApprovalCount)
                    }

                    {/* Engineering */}
                    {canAccessEngineeringModule(user) &&
                        renderSubmenu("engineering", <FiPenTool />, "Engineering", [
                            { to: "/newpartcreation", text: "New Part Creation" },
                            { to: "/masterdata?mode=edit", text: "BOM Creation" },
                            { to: "/revision-history", text: "Revision History" },
                            { to: "/production-configuration", text: "Production Configuration" }
                        ])
                    }

                    {/* Tooling */}
                    {canAccessToolingModule(user) && toolingLinks.length > 0 &&
                        renderSubmenu("tooling", <FiTool />, "Tooling", toolingLinks)
                    }

                    {/* Inventory Management */}
                    {(canAccessInventoryPortalItem(user, 'master-raw-material') ||
                        canAccessInventoryPortalItem(user, 'master-finished-goods') ||
                        canAccessInventoryPortalItem(user, 'master-location') ||
                        canAccessInventoryPortalItem(user, 'activate-storage') ||
                        canAccessInventoryPortalItem(user, 'receiving-summary') ||
                        canAccessInventoryPortalItem(user, 'rm-transfer-summary') ||
                        canAccessInventoryPortalItem(user, 'fg-transfer-summary') ||
                        canAccessInventoryPortalItem(user, 'scrapping-summary')) &&
                        renderSubmenu("inventory", <FiDatabase />, "Inventory", [
                            ...(canAccessInventoryPortalItem(user, 'master-location') ? [{ to: "/master-location", text: "Master Location" }] : []),
                            ...(canAccessInventoryPortalItem(user, 'activate-storage') ? [{ to: "/activate-storage", text: "Activate Storage" }] : []),
                            ...(canAccessInventoryPortalItem(user, 'master-raw-material') ? [{ to: "/master-raw-material", text: "Master Raw Material" }] : []),
                            ...(canAccessInventoryPortalItem(user, 'master-finished-goods') ? [{ to: "/master-finished-goods", text: "Master Finished Goods" }] : []),
                            ...(canAccessInventoryPortalItem(user, 'receiving-summary') ? [{ to: "/receiving-summary", text: "Receiving Summary" }] : []),
                            ...(canAccessInventoryPortalItem(user, 'rm-transfer-summary') ? [{ to: "/rm-transfer-summary", text: "RM Transfer Summary" }] : []),
                            ...(canAccessInventoryPortalItem(user, 'fg-transfer-summary') ? [{ to: "/fg-transfer-summary", text: "FG Transfer Summary" }] : []),
                            ...(canAccessInventoryPortalItem(user, 'fg-transfer-summary') ? [{ to: "/fg-delivery-summary", text: "FG Delivery Summary" }] : []),
                            ...(canAccessInventoryPortalItem(user, 'scrapping-summary') ? [{ to: "/scrapping-summary", text: "Scrapping Summary" }] : [])
                        ])
                    }

                    {/* Accounting & Finance */}
                    {/* Sales Module */}
                    {(canAccessSalesModule(user) || user.username === 'test') && (
                        renderSubmenu("sales_module", <FiShoppingCart />, "Sales", [
                            // Customer: visible to BUU (full) + Shipping (view link) + BUM (view link) + Accountant + Admin
                            ...(
                                isBusinessUnitUser(user) || isShippingUser(user) || isBusinessUnitManager(user) ||
                                    canAccessAccounting(user) || isSuperAdmin(user)
                                    ? [{ to: "/contacts/customers", text: "Customer Maintenance" }]
                                    : []
                            ),
                            // Quotation: BUU (full) + BUM (view) + Accountant + Admin
                            ...(
                                isBusinessUnitUser(user) || isBusinessUnitManager(user) ||
                                    canAccessAccounting(user) || isSuperAdmin(user)
                                    ? [{ to: "/sales/quotations", text: "Quotation" }]
                                    : []
                            ),
                            // Sales Order: BUU (full) + BUM (view) + Accountant + Admin
                            ...(
                                isBusinessUnitUser(user) || isBusinessUnitManager(user) ||
                                    canAccessAccounting(user) || isSuperAdmin(user)
                                    ? [{ to: "/sales/sales-orders", text: "Sales Order" }]
                                    : []
                            ),
                            // Invoice: Shipping (full) + BUM (view) + Accountant + Admin
                            ...(
                                isShippingUser(user) || isBusinessUnitManager(user) ||
                                    canAccessAccounting(user) || isSuperAdmin(user)
                                    ? [{ to: "/sales/invoices", text: "Invoice" }]
                                    : []
                            ),
                            // Delivery Order: Shipping (full) + BUM (view) + Accountant + Admin
                            ...(
                                isShippingUser(user) || isBusinessUnitManager(user) ||
                                    canAccessAccounting(user) || isSuperAdmin(user)
                                    ? [{ to: "/sales/delivery-orders", text: "Delivery Order" }]
                                    : []
                            ),
                            // Sales Approvals: BUM (full) + Accountant + Admin
                            ...(
                                isBusinessUnitManager(user) || canAccessAccounting(user) || isSuperAdmin(user)
                                    ? [{ to: "/accounting-approvals/sales-approvals", text: "Sales Approval", badge: pendingSalesCount }]
                                    : []
                            ),
                        ], pendingSalesCount)
                    )}

                    {/* Purchasing Module */}
                    {(canAccessPurchaseModule(user) || user.username === 'test') && (
                        renderSubmenu("purchasing_module", <FiBox />, "Purchasing", [
                            // Supplier: Purchasing User (full) + Purchasing Manager (view) + Accountant + Admin
                            ...(
                                isPurchasingUser(user) || isPurchasingManager(user) ||
                                    canAccessAccounting(user) || isSuperAdmin(user)
                                    ? [{ to: "/contacts/suppliers", text: "Supplier Maintenance" }]
                                    : []
                            ),
                            // Purchase Order: Purchasing User (full) + Purchasing Manager (view) + Accountant + Admin
                            ...(
                                isPurchasingUser(user) || isPurchasingManager(user) ||
                                    canAccessAccounting(user) || isSuperAdmin(user)
                                    ? [{ to: "/purchases/purchase-orders", text: "Purchase Order" }]
                                    : []
                            ),
                            // Purchase Approvals: Purchasing Manager (full) + Accountant + Admin
                            ...(
                                isPurchasingManager(user) || canAccessAccounting(user) || isSuperAdmin(user)
                                    ? [{ to: "/accounting-approvals/purchase-approvals", text: "Purchasing Approval", badge: pendingPurchaseCount }]
                                    : []
                            ),
                        ], pendingPurchaseCount)
                    )}

                    {/* Financial & Accounting Module */}
                    {(canAccessAccounting(user) || user.username === 'test') && (
                        renderSubmenu("financial_accounting_module", <FiDollarSign />, "Financial & Accounting", [
                            { to: "/contacts/company", text: "Company Maintenance" },
                            { to: "/finance/chart-of-accounts", text: "Chart of Accounts" },
                            {
                                text: "Purchase Invoices",
                                subLinks: [
                                    { to: "/finance/direct-purchase-invoices", text: "Direct Purchase Invoice" },
                                    { to: "/finance/indirect-purchase-invoices", text: "Indirect Purchase Invoice" }
                                ]
                            },
                            { to: "/finance/payment-vouchers", text: "Payment Voucher" },
                            {
                                text: "Credit & Debit Notes",
                                subLinks: [
                                    { to: "/finance/credit-notes", text: "Sales Credit Note" },
                                    { to: "/finance/debit-notes", text: "Sales Debit Note" },
                                    { to: "/finance/purchase-credit-notes", text: "Purchase Credit Note" },
                                    { to: "/finance/purchase-debit-notes", text: "Purchase Debit Note" },
                                ]
                            },
                            {
                                text: "Costing",
                                subLinks: [
                                    { text: "Project Costing" },
                                    { text: "Operation Costing" },
                                    { text: "Budget Management" }
                                ]
                            },
                            { to: "/accounting-approvals/finance-approvals", text: "Finance Approvals", badge: pendingFinanceCount }
                        ], pendingFinanceCount)
                    )}

                    {/* Doc Con */}
                    {(canAccessQCModule(user) || user.username === 'test') &&
                        renderSubmenu("doccon", <FiFolder />, "Doc Con", [
                            { to: "/doccon/manufacturing", text: "Manufacturing" },
                            { to: "/doccon/drawing", text: "Drawing" },
                            { to: "/doccon/quality", text: "Quality" },
                            { to: "/doccon/flow", text: "Flow" },
                            { to: "/doccon/control", text: "Control Plan" },
                            { to: "/doccon/parameter", text: "Parameter" }
                        ])
                    }

                    {/* Admin Only */}
                    {isAdmin(user) && renderNavLink("/datamanagement", <FiUsers />, "Data Management")}
                    {renderSubmenu("settings", <FiSettings />, "Settings", [
                        { to: "/profile", text: "Profile" },
                        { to: "/currency-settings", text: "Currency" },
                        ...((isAdmin(user) || isSuperAdmin(user)) ? [{ to: "/targets", text: "Targets and Utilities" }] : []),
                        ...(canAccessUserSettings(user) ? [{ to: "/user-settings", text: "Users" }] : []),
                        ...(canAccessModuleAccessibility(user) ? [{ to: "/module-accessibility", text: "Module Accessibility" }] : []),
                        ...(canAccessApprovalRouting(user) ? [{ to: "/settings", text: "Approval Routing" }] : [])
                    ])}
                </div>

                <div className="sidebar-footer">
                    <div className="user-info">
                        <div className="user-avatar">
                            {user.firstName ? user.firstName.charAt(0) : <FiUser />}
                        </div>
                        {!collapsed && (
                            <div className="user-details">
                                <div className="user-name">
                                    {user.firstName && user.lastName
                                        ? `${user.firstName} ${user.lastName}`
                                        : user.username}
                                </div>
                                <div className="user-role">
                                    {isAdmin(user) ? 'Administrator' : 'User'}
                                </div>
                            </div>
                        )}
                    </div>
                    <Link to="/login" className="nav-link-custom" style={{ padding: '10px 0', border: 'none' }}>
                        <div className="nav-icon"><FiLogOut /></div>
                        <span className="nav-text">Logout</span>
                    </Link>
                </div>
            </aside>
            {mobileOpen && <div className="sidebar-overlay" onClick={closeMobileSidebar}></div>}
        </>
    );
};

export default Sidebar;
